import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import { fmt, short } from "../format";
import { useConfirmAction } from "./useConfirmAction";
import type { ReviewQueueData, Scraper } from "../types";

/**
 * Everything here is waiting on a human decision. A canary is the one gold thing
 * in the product: a repair that exists and needs approving.
 */
export function ReviewQueue() {
  const [auto, setAuto] = useState(false);
  const { data, loading, error, reload } = useAsync<ReviewQueueData>(
    () => api("/review/queue"),
    [],
    auto ? 10_000 : undefined,
  );
  // The person deciding whether to promote a canary needs to see WHICH scraper.
  // /review/queue hashes it, so resolve names here.
  const { data: scrapers } = useAsync<Scraper[]>(() => api("/scrapers"), []);
  const nameOf = (id: string | undefined) =>
    scrapers?.find((s) => s.id === id)?.name ?? short(id);
  const confirmAct = useConfirmAction();
  const [actErr, setActErr] = useState<string | null>(null);

  function act(path: string, confirmMsg?: string) {
    const run = async () => {
      await api(path, { method: "POST" });
      reload();
    };
    if (confirmMsg) {
      // Confirmed actions (e.g. reject) go through the in-app dialog; its failure shows inline there.
      confirmAct.ask({ title: confirmMsg, danger: true, run });
    } else {
      // Direct actions (approve / unpause) — surface any failure in the page banner, not a popup.
      setActErr(null);
      run().catch((e) => setActErr("Action failed: " + (e instanceof Error ? e.message : String(e))));
    }
  }

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  const { pausedScrapers, escalatedAttempts, canaries } = data;
  const empty =
    pausedScrapers.length === 0 && escalatedAttempts.length === 0 && canaries.length === 0;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Waiting</h1>
        <div className="row-gap">
          <label className="inline muted">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto
          </label>
          <RefreshButton onClick={reload} />
        </div>
      </div>

      {actErr && (
        <div className="err" role="alert">
          {actErr}
        </div>
      )}

      {/* An empty queue is a flat, unbroken hairline. Nothing is waiting. */}
      {empty && (
        <div className="empty onboard">
          <svg
            width="100%"
            height="12"
            viewBox="0 0 400 12"
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ display: "block", marginBottom: "var(--sp5)" }}
          >
            <line
              x1="0" y1="6" x2="400" y2="6"
              stroke="var(--iron)" strokeOpacity="0.35" strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <h3>Nothing is waiting</h3>
          <p>Every repair has been decided and no scraper is paused.</p>
        </div>
      )}

      {canaries.length > 0 && (
        <section>
          <h3>
            Fixes awaiting a decision <span className="badge">{canaries.length}</span>
          </h3>
          <p className="fleet-line mt">
            Amendie repaired these itself and proved them on live runs. Promoting makes the repair
            the active extractor.
          </p>
          <div className="table-scroll mt">
            <table>
              <thead>
                <tr>
                  <th>Scraper</th>
                  <th>Version</th>
                  <th>By</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {canaries.map((c) => (
                  <tr key={c.id} className="is-heal">
                    <td>{nameOf(c.scraper_id)}</td>
                    <td className="mono">{short(c.id)}</td>
                    <td>{c.created_by === "ai" ? <span className="pill heal">Amendie</span> : c.created_by}</td>
                    <td className="nowrap">{fmt(c.created_at)}</td>
                    <td className="acts">
                      <button
                        className="btn seam sm"
                        onClick={() => act(`/scrapers/${c.scraper_id}/versions/${c.id}/promote`)}
                      >
                        Approve fix
                      </button>
                      <button
                        className="btn danger sm"
                        onClick={() =>
                          act(
                            `/scrapers/${c.scraper_id}/versions/${c.id}/reject`,
                            "Reject this repair? It will be archived.",
                          )
                        }
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {pausedScrapers.length > 0 && (
        <section>
          <h3>
            Paused scrapers <span className="badge">{pausedScrapers.length}</span>
          </h3>
          <p className="fleet-line mt">
            Repair attempts were exhausted — these need a look before they collect again.
          </p>
          <div className="table-scroll mt">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>ID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pausedScrapers.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td className="mono">{short(s.id)}</td>
                    <td className="acts">
                      <button className="btn ghost sm" onClick={() => act(`/scrapers/${s.id}/unpause`)}>
                        Unpause
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {escalatedAttempts.length > 0 && (
        <section>
          <h3>
            Escalated repair attempts <span className="badge">{escalatedAttempts.length}</span>
          </h3>
          <div className="table-scroll mt">
            <table>
              <thead>
                <tr>
                  <th>Scraper</th>
                  <th>Outcome</th>
                  <th>Attempt</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {escalatedAttempts.map((a) => (
                  <tr key={a.id}>
                    <td>{nameOf(a.scraper_id)}</td>
                    <td>{a.outcome ?? "—"}</td>
                    <td>{a.attempt ?? "—"}</td>
                    <td className="nowrap">{fmt(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {confirmAct.modal}
    </div>
  );
}
